import React, { FC, useId } from 'react';
import { connect } from 'react-redux';
import { useMutation, useQuery } from 'urql';

import ComposeSection from '../../../components/MUI/Compose/ComposeSection';
import Labels from '../../../components/MUI/Compose/Labels';
import {
  Label,
  LabelCreateDocument,
  LabelsDocument,
} from '../../../generated/graphql';
import translate from '../../../i18n/translate';
import { ComposeModeParam } from '../../../models/pageParams';
import { reportApiErrors } from '../../../utils/error';

import useComposeLinking from '../hooks/useComposeLinking';

import { mapDispatchToProps, mapStateToProps } from './redux';
import { Props } from './types';

/**
 * Metadata for a message, render labels
 *
 * @param props                     Props passed to the component
 * @param props.dataSearchValue     Current search value
 * @param props.dataSelected        Labels currently selected (not yet saved)
 * @param props.debugLoading        Whether to permanently enable loading state
 * @param props.requestLabelAdd     Request that a label is added to selection
 * @param props.requestLabelRemove  Request that a label is removed from selection
 * @param props.requestLabelsSave   Request that dataSelected is stored in dataSaved
 * @param props.requestLabelsSearch Request update to search term
 * @returns                         The component itself
 */
const ComposeMetaLabelsContainer: FC<Props> = ({
  dataSearchValue,
  dataSelected,
  debugLoading,
  requestLabelAdd,
  requestLabelRemove,
  requestLabelsSave,
  requestLabelsSearch,
}) => {
  const composeMode: ComposeModeParam = 'label';

  const { isActive, linkToggleProps } = useComposeLinking(composeMode);

  const [{ data, fetching }] = useQuery({
    query: LabelsDocument,
    variables: {},
  });
  const [, labelCreateMutation] = useMutation(LabelCreateDocument);

  const dataAll = data?.labels ?? [];

  /**
   * Request that a label is created
   *
   * @param text  The label text
   * @param color The label color
   */
  const requestCreate = (text: Label['text'], color: Label['color']) => {
    labelCreateMutation({ color, text })
      .then(newLabel => {
        if (newLabel.data?.labelCreate !== undefined) {
          requestLabelAdd(newLabel.data.labelCreate.id);
        }
      })
      .catch(reportApiErrors);
  };

  const idHeading = useId();
  const textHeading = translate('COMPOSE__PLACEHOLDER__LABEL');

  /**
   * Change label state
   *
   * @param labelId Label id
   * @param state   Whether to (un)select the label
   */
  const requestToggle = (labelId: Label['id'], state: boolean) => {
    if (state) {
      requestLabelAdd(labelId);
    } else {
      requestLabelRemove(labelId);
    }
    requestLabelsSave();
  };

  return (
    <ComposeSection
      idHeading={idHeading}
      inputCustom={null}
      isActive={isActive}
      linkToggleProps={linkToggleProps}
      textHeading={textHeading}
      value={textHeading}
    >
      <Labels
        dataAll={dataAll}
        dataSearchValue={dataSearchValue}
        dataSelected={dataSelected}
        isLoading={debugLoading || fetching}
        requestCreate={requestCreate}
        requestSearch={requestLabelsSearch}
        requestToggle={requestToggle}
      />
    </ComposeSection>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ComposeMetaLabelsContainer);
